import * as React from "react";
import { useWindowManager, type ManagedWindow } from "../WindowManagerContext";
import { hinweisen } from "../../redesign/bestaetigen";
import { BRAND } from "../../risikoanalyseStep/tokens";
import { meldeBrowserFensterAn } from "./fensterRegister";
import { richtePopupEin, type PopupGeruest } from "./popupDokument";
import { fensterMerkmale, geometrieAmFenster, geometrieNeben, ladeGeometrie, merkeGeometrie } from "./geometrie";
import { zeigeKmvs } from "./kmvsZeigen";

export type OffenesBrowserFenster = { popup: Window; geruest: PopupGeruest };

/** Leere Seite aus `public/` — eine echte KMVS-Adresse statt `about:blank` in der Adresszeile. */
const HUELLE = "/browserfenster.html";

/** Ist die Hülle schon da? (Ein Browserfenster gleichen Namens kann noch offen sein.) */
function huelleGeladen(popup: Window): boolean {
  try {
    return popup.location.pathname === HUELLE && popup.document.readyState === "complete";
  } catch {
    return false;
  }
}

/**
 * Das eigene Browserfenster eines KMVS-Fensters (User 26.10.2026).
 *
 * Gesteuert über `ManagedWindow.browserFenster`: Wird es `true` (Knopf in der
 * Titelleiste), öffnet der Hook ein leeres Browserfenster und hängt den
 * Fensterinhalt dorthin um; wird es `false` (Kopfleiste aufs Hauptfenster
 * gezogen), kommt der Inhalt zurück und das Browserfenster geht zu.
 *
 * Der Inhalt selbst bleibt dabei gemountet — er steckt in `inhaltEl`, einem
 * festen Container, den React per Portal füllt. Umgehängt wird nur dieser
 * Container; so überstehen Eingaben, Entwürfe und Scrollstände den Umzug.
 *
 * Schließt der Nutzer das Browserfenster selbst, gilt das als „Fenster
 * schließen“ — mit ungespeicherten Eingaben kommt es aber zurück ins
 * Hauptfenster und fragt dort nach, statt sie still zu verwerfen.
 *
 * „Zurück in KMVS“ schließt das Fenster ebenfalls, das Browserfenster bleibt
 * dabei aber offen und zeigt KMVS (`kmvsZeigenRef`, `kmvsZeigen.ts`).
 */
export function useBrowserFenster(
  w: ManagedWindow,
  optionen: {
    /** Der Körper des schwebenden Fensters — dort steht der Inhalt im Hauptfenster. */
    koerperEl: HTMLElement | null;
    /** Fenster-Layer im Hauptfenster (für die Startlage des Browserfensters). */
    layerEl: () => HTMLElement | null;
    /** Schließen mit der üblichen Rückfrage (FloatingWindow). */
    schliessenAnfragen: () => void;
    /**
     * Gesetzt, wenn das Fenster über „Zurück in KMVS“ zugeht: Das
     * Browserfenster zeigt dann KMVS, statt sich zu schließen. Gilt einmal.
     */
    kmvsZeigenRef: React.MutableRefObject<boolean>;
  },
) {
  const { setWindowBrowserFenster, closeWindow, hasWindowCloseGuard } = useWindowManager();
  const [inhaltEl] = React.useState(() => {
    const el = document.createElement("div");
    // Keine eigene Box: Der Inhalt liegt im Layout weiter direkt im Körper.
    el.style.display = "contents";
    el.setAttribute("data-fenster-inhalt", "");
    return el;
  });
  const [offen, setOffen] = React.useState<OffenesBrowserFenster | null>(null);
  const [eingehaengt, setEingehaengt] = React.useState(false);

  const offenRef = React.useRef<OffenesBrowserFenster | null>(null);
  /** Browserfenster, dessen Hülle gerade noch lädt. */
  const ladendRef = React.useRef<Window | null>(null);
  const abmeldenRef = React.useRef<(() => void) | null>(null);
  const aktuell = React.useRef({ w, ...optionen });
  aktuell.current = { w, ...optionen };

  const haengeEin = React.useCallback(
    (ziel: HTMLElement | null) => {
      if (ziel && inhaltEl.parentNode !== ziel) ziel.appendChild(inhaltEl);
    },
    [inhaltEl],
  );

  /** Browserfenster abbauen; der Inhalt geht vorher zurück ins Hauptfenster. */
  const abbauen = React.useCallback(
    (schliessen: boolean) => {
      const o = offenRef.current;
      if (!o) return;
      offenRef.current = null;
      merkeGeometrie(o.popup);
      haengeEin(aktuell.current.koerperEl);
      abmeldenRef.current?.();
      abmeldenRef.current = null;
      o.geruest.aufraeumen();
      const kmvs = aktuell.current.kmvsZeigenRef.current;
      aktuell.current.kmvsZeigenRef.current = false;
      if (schliessen && !o.popup.closed) {
        if (kmvs) zeigeKmvs(o.popup);
        else o.popup.close();
      }
      setOffen(null);
    },
    [haengeEin],
  );

  // Inhalt dorthin hängen, wo das Fenster gerade dargestellt wird. Erst danach
  // wird er gerendert — seine Effekte finden ihn so schon im Dokument vor.
  React.useLayoutEffect(() => {
    const ziel = offen ? offen.geruest.koerper : optionen.koerperEl;
    if (!ziel) return;
    haengeEin(ziel);
    setEingehaengt(true);
  }, [offen, optionen.koerperEl, haengeEin]);

  // Auslagern bzw. zurückholen, wie es der Fenster-Zustand verlangt.
  React.useEffect(() => {
    if (w.browserFenster && !offenRef.current && !ladendRef.current) {
      const cur = aktuell.current.w;
      const layer = aktuell.current.layerEl()?.getBoundingClientRect() ?? { left: 0, top: 0 };
      const g = geometrieNeben(cur.browserFensterNeben) ?? ladeGeometrie() ?? geometrieAmFenster(layer, cur, window);
      // Aus einem Browserfenster heraus geöffnet (Vorschau): über DESSEN
      // `open`. Geklickt wurde dort; das `open` des Hauptfensters lässt
      // Chrome/Edge nur beim ersten Mal durch und blockiert danach (gemessen
      // in Chrome for Testing 1217, 01.11.2026).
      const oeffner = cur.browserFensterNeben && !cur.browserFensterNeben.closed ? cur.browserFensterNeben : window;
      const popup = oeffner.open(HUELLE, `kmvs-fenster-${cur.id}`, fensterMerkmale(g));
      if (!popup) {
        setWindowBrowserFenster(cur.id, false);
        void hinweisen({
          titel: "Browserfenster wurde blockiert",
          text:
            "Der Browser hat das neue Fenster nicht geöffnet. Rechts in der Adresszeile erscheint dafür ein Symbol: " +
            "dort „Popups und Weiterleitungen von KMVS immer zulassen“ wählen und es noch einmal versuchen.",
        });
        return;
      }
      ladendRef.current = popup;
      const einrichten = () => {
        if (ladendRef.current !== popup) return;
        ladendRef.current = null;
        window.clearInterval(wache);
        const o: OffenesBrowserFenster = { popup, geruest: richtePopupEin(popup, aktuell.current.w.title, BRAND.bg) };
        abmeldenRef.current = meldeBrowserFensterAn(popup);
        offenRef.current = o;
        setOffen(o);
      };
      // Geschlossen, bevor die Hülle geladen war: bleibt im Hauptfenster.
      const wache = window.setInterval(() => {
        if (!popup.closed || ladendRef.current !== popup) return;
        ladendRef.current = null;
        window.clearInterval(wache);
        setWindowBrowserFenster(aktuell.current.w.id, false);
      }, 300);
      if (huelleGeladen(popup)) einrichten();
      else popup.addEventListener("load", einrichten, { once: true });
    } else if (!w.browserFenster && offenRef.current) {
      abbauen(true);
    } else if (!w.browserFenster && ladendRef.current) {
      const ladend = ladendRef.current;
      ladendRef.current = null;
      if (!ladend.closed) ladend.close();
    }
  }, [w.browserFenster, setWindowBrowserFenster, abbauen]);

  // Der Nutzer schließt das Browserfenster, oder das Hauptfenster geht zu.
  React.useEffect(() => {
    if (!offen) return;
    const { popup } = offen;
    const vomNutzerGeschlossen = () => {
      if (offenRef.current !== offen) return;
      abbauen(false);
      const cur = aktuell.current.w;
      if (cur.dirty || hasWindowCloseGuard(cur.id)) {
        // Ungespeichertes nicht verwerfen: zurück ins Hauptfenster, dort fragen.
        setWindowBrowserFenster(cur.id, false);
        aktuell.current.schliessenAnfragen();
      } else {
        closeWindow(cur.id);
      }
    };
    popup.addEventListener("pagehide", vomNutzerGeschlossen);
    // Falls `pagehide` ausbleibt (Absturz, Browser-Eigenheit).
    const wache = window.setInterval(() => {
      if (popup.closed) vomNutzerGeschlossen();
    }, 1000);
    const hauptfensterGehtZu = () => {
      if (!popup.closed) popup.close();
    };
    window.addEventListener("pagehide", hauptfensterGehtZu);
    return () => {
      popup.removeEventListener("pagehide", vomNutzerGeschlossen);
      window.clearInterval(wache);
      window.removeEventListener("pagehide", hauptfensterGehtZu);
    };
  }, [offen, abbauen, closeWindow, hasWindowCloseGuard, setWindowBrowserFenster]);

  // Titel mitführen.
  React.useEffect(() => {
    if (offen && !offen.popup.closed) offen.popup.document.title = w.title;
  }, [offen, w.title]);

  // Wird das Fenster erneut geöffnet oder nach vorn geholt, das Browserfenster
  // in den Vordergrund holen.
  React.useEffect(() => {
    if (offenRef.current && !offenRef.current.popup.closed) offenRef.current.popup.focus();
  }, [w.zIndex]);

  // Fenster in KMVS geschlossen: Browserfenster mit zu.
  React.useEffect(
    () => () => {
      abbauen(true);
      const ladend = ladendRef.current;
      ladendRef.current = null;
      if (ladend && !ladend.closed) ladend.close();
    },
    [abbauen],
  );

  return { inhaltEl, eingehaengt, offen };
}
